import { Component } from '@theme/component';

/**
 * A minimal, dependency-free horizontal scroller for the Quick Links carousel.
 *
 * Deliberately does NOT use the shared `slideshow-component` (assets/slideshow.js).
 * That component attaches `mouseenter`/`pointerenter` listeners to itself (to pause
 * autoplay and reveal arrows on hover) as well as a `mousedown` drag handler on its
 * scroller. On iOS Safari, an ancestor carrying hover-type listeners makes the first
 * tap on a descendant link only simulate ':hover' - the actual click/navigation only
 * fires on a second tap. Quick Links doesn't need autoplay, dragging, or looping, so
 * it uses plain native scroll-snap scrolling plus click-to-scroll arrow buttons,
 * which have no hover listeners at all and don't trigger that quirk.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class QuickLinksCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.#updateArrowState, { passive: true });

    // The carousel can connect while it has no layout (inside a closed menu with
    // `content-visibility: auto`, a closed <details>, or display:none), so re-check
    // the arrows whenever the scroller gets or changes its size.
    this.#resizeObserver = new ResizeObserver(this.#updateArrowState);
    this.#resizeObserver.observe(scroller);

    this.#updateArrowState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.scroller?.removeEventListener('scroll', this.#updateArrowState);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Scrolls to the previous page of links.
   * @param {Event} [event]
   */
  previous = (event) => {
    event?.preventDefault();
    this.#scrollByPage(-1);
  };

  /**
   * Scrolls to the next page of links.
   * @param {Event} [event]
   */
  next = (event) => {
    event?.preventDefault();
    this.#scrollByPage(1);
  };

  /**
   * @param {1 | -1} direction
   */
  #scrollByPage(direction) {
    const { scroller } = this.refs;
    scroller.scrollBy({ left: scroller.clientWidth * 0.8 * direction * this.#inlineSign, behavior: 'smooth' });
  }

  /** 1 in LTR, -1 in RTL, where `scrollLeft` runs from 0 to negative values. */
  get #inlineSign() {
    return getComputedStyle(this.refs.scroller).direction === 'rtl' ? -1 : 1;
  }

  #updateArrowState = () => {
    const { scroller, previous, next } = this.refs;
    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const position = Math.abs(scroller.scrollLeft);

    if (previous) previous.disabled = position <= 1;
    if (next) next.disabled = position >= maxScroll - 1;
  };
}

if (!customElements.get('quick-links-carousel')) {
  customElements.define('quick-links-carousel', QuickLinksCarousel);
}
